/**
 * MATAFOME — API DE CARDÁPIO, PRODUTOS, COMPLEMENTOS E CATÁLOGO MESTRE
 */

import { safeApiFetch, getAuthHeaders, API_BASE } from './core.api';

export async function fetchCategories() {
    const res = await safeApiFetch('/api/menu/categories', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar categorias.');
    return data.categories || [];
}

export async function saveCategory(category) {
    const isEdit = Boolean(category.id && !String(category.id).startsWith('cat-temp-'));
    const method = isEdit ? 'PUT' : 'POST';
    const path = isEdit ? `/api/menu/categories/${category.id}` : `/api/menu/categories`;
    const payload = {};
    if (category.name !== undefined) payload.name = String(category.name).trim();
    if (category.icon !== undefined) payload.icon = category.icon;
    if (category.image !== undefined) payload.image = category.image;
    if (category.description !== undefined) payload.description = category.description;
    if (category.display_order !== undefined) payload.display_order = parseInt(category.display_order, 10);
    else if (category.order !== undefined) payload.display_order = parseInt(category.order, 10);
    if (category.active !== undefined) payload.active = (category.active ? 1 : 0);
    if (!isEdit && category.id) payload.id = category.id;

    const res = await safeApiFetch(path, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar categoria.');
    return data.category;
}

export async function deleteCategory(categoryId) {
    const res = await safeApiFetch(`/api/menu/categories/${categoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir categoria.');
    return data;
}

export async function reorderCategories(orders) {
    const res = await safeApiFetch('/api/menu/categories/reorder', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ orders })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao reordenar categorias.');
    return data;
}

export async function fetchProducts() {
    const res = await safeApiFetch('/api/menu/products', { headers: getAuthHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar produtos.');
    return data.products || [];
}

export async function saveProduct(product) {
    const isEdit = Boolean(product.id && !String(product.id).startsWith('prod-temp-'));
    const method = isEdit ? 'PUT' : 'POST';
    const path = isEdit ? `/api/menu/products/${product.id}` : `/api/menu/products`;
    const payload = {
        ...product,
        category_id: product.category_id || product.categoryId,
        price: parseFloat(product.price) || 0,
        promo_price: product.promoPrice !== undefined ? (parseFloat(product.promoPrice) || 0) : (parseFloat(product.promo_price) || 0),
        track_stock: product.track_stock !== undefined ? Boolean(product.track_stock) : Boolean(product.trackStock),
        stock_quantity: product.stock_quantity !== undefined 
            ? Math.max(0, parseInt(product.stock_quantity || 0, 10)) 
            : (product.stockQuantity !== undefined ? Math.max(0, parseInt(product.stockQuantity || 0, 10)) : 0)
    };
    const res = await safeApiFetch(path, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar produto.');
    return data.product;
}

export async function toggleProductStatus(productId) {
    const res = await safeApiFetch(`/api/menu/products/${productId}/toggle`, {
        method: 'PATCH',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao alternar status do produto.');
    return data.product;
}

export async function deleteProduct(productId) {
    const res = await safeApiFetch(`/api/menu/products/${productId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir produto.');
    return data;
}

// ----------------------------------------------------
// CATÁLOGO MESTRE (PÚBLICO & UNIVERSAL)
// ----------------------------------------------------

export function resolveCatalogImageUrl(photoPath) {
    if (!photoPath) return '';
    if (photoPath.startsWith('http://') || photoPath.startsWith('https://') || photoPath.startsWith('data:')) {
        return photoPath;
    }
    const cleanPath = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        if (API_BASE && !API_BASE.includes(window.location.host)) {
            return `${API_BASE}${cleanPath}`;
        }
    }
    return cleanPath;
}

export async function fetchMasterCatalogNichos() {
    const res = await safeApiFetch('/api/catalogo-mestre/nichos');
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao carregar nichos do catálogo mestre.');
    return data;
}

export async function fetchMasterCatalogProducts({ q = '', nicho = '', page = 1, limit = 24 } = {}) {
    const params = new URLSearchParams();
    if (q && q.trim()) params.set('q', q.trim());
    if (nicho && nicho !== 'all') params.set('nicho', nicho);
    if (page) params.set('page', String(page));
    if (limit) params.set('limit', String(Math.min(50, limit)));

    const res = await safeApiFetch(`/api/catalogo-mestre/produtos?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao buscar produtos do catálogo mestre.');
    return data;
}


// ----------------------------------------------------
// PEDIDOS & KANBAN
// ----------------------------------------------------

export async function importMenuWithAi(fileBase64, mimeType) {
    const res = await safeApiFetch('/api/menu/import-ai', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileBase64, mimeType }),
        timeout: 90000
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao processar cardápio com IA.');
    return data;
}

export async function confirmAiMenuImport(categories, complementGroups = []) {
    const res = await safeApiFetch('/api/menu/import-ai-confirm', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ categories, complementGroups })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar cardápio importado.');
    return data;
}

// ==========================================
// GESTÃO DE ENTREGADORES & ROLETA DE DESPACHO
// ==========================================

export async function fetchComplementGroups() {
    const res = await safeApiFetch('/api/menu/complement-groups', {
        method: 'GET',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao listar grupos de complementos.');
    return data.groups || [];
}

export async function createComplementGroup(groupData) {
    const res = await safeApiFetch('/api/menu/complement-groups', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(groupData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao criar grupo de complementos.');
    return data.group;
}

export async function createComplementGroupsBatch(groups) {
    const res = await safeApiFetch('/api/menu/complement-groups/batch', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ groups })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao criar grupos de complementos em lote.');
    return data.groups || [];
}

export async function updateComplementGroup(groupId, groupData) {
    const res = await safeApiFetch(`/api/menu/complement-groups/${groupId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(groupData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar grupo de complementos.');
    return data.group;
}

export async function toggleComplementGroup(groupId) {
    const res = await safeApiFetch(`/api/menu/complement-groups/${groupId}/toggle`, {
        method: 'PATCH'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao alternar grupo.');
    return data.group;
}

export async function deleteComplementGroup(groupId) {
    const res = await safeApiFetch(`/api/menu/complement-groups/${groupId}`, {
        method: 'DELETE'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir grupo.');
    return data;
}

export async function addComplementItem(groupId, itemData) {
    const res = await safeApiFetch(`/api/menu/complement-groups/${groupId}/items`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(itemData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao adicionar item.');
    return data.item;
}

export async function updateComplementItem(itemId, itemData) {
    const res = await safeApiFetch(`/api/menu/complement-items/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(itemData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar item.');
    return data.item;
}

export async function toggleComplementItem(itemId) {
    const res = await safeApiFetch(`/api/menu/complement-items/${itemId}/toggle`, {
        method: 'PATCH'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao alternar item.');
    return data.item;
}

export async function deleteComplementItem(itemId) {
    const res = await safeApiFetch(`/api/menu/complement-items/${itemId}`, {
        method: 'DELETE'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao excluir item.');
    return data;
}

export async function fetchProductComplementGroups(productId) {
    const res = await safeApiFetch(`/api/menu/products/${productId}/complement-groups`, {
        method: 'GET',
        headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao listar grupos do produto.');
    return data.groups || [];
}

export async function syncProductComplementGroups(productId, groupIds) {
    const res = await safeApiFetch(`/api/menu/products/${productId}/complement-groups`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ groupIds })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao sincronizar grupos.');
    return data;
}

export async function removeProductComplementGroup(productId, groupId) {
    const res = await safeApiFetch(`/api/menu/products/${productId}/complement-groups/${groupId}`, {
        method: 'DELETE'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erro ao remover associação.');
    return data;
}
